/**
 * Per-microsite content. Drives the home, services overview, 3-5 service
 * detail pages, about, contact, and FAQ — keeping each microsite topic-tuned
 * rather than a clone of obriencontractingusa.com.
 *
 * Tool exposure is intentionally narrow: every microsite gets /quick-estimate
 * and /contact; only topic-relevant extras are exposed via `extraTools`.
 */
import type { BrandKey } from "./brandRegistry";

export interface FAQ {
  q: string;
  a: string;
}

export interface ServiceDetail {
  slug: string;
  name: string;
  title: string;
  metaDescription: string;
  h1: string;
  intro: string;
  bullets: string[];
  body: string[]; // paragraphs of unique copy
  faqs: FAQ[];
}

export interface MicroSiteContent {
  /** Topic-tuned hero copy */
  heroH1: string;
  heroSubhead: string;
  heroBullets: string[];
  /** "Services overview" page copy */
  servicesH1: string;
  servicesIntro: string;
  servicesOutro: string;
  /** Optional pillar-page body paragraphs rendered on the /services hub. */
  pillarBody?: string[];
  /** 3-5 service detail pages */
  services: ServiceDetail[];
  /** About page narrative */
  aboutH1: string;
  aboutBody: string[];
  /** FAQ page entries */
  faqs: FAQ[];
  /** Topic-relevant shared OBC tools exposed under this brand. */
  extraTools: Array<"roof-visualizer" | "leak-detection" | "storm-damage-analyzer">;
}

const detail = (
  slug: string,
  name: string,
  title: string,
  metaDescription: string,
  h1: string,
  intro: string,
  bullets: string[],
  body: string[],
  faqs: FAQ[],
): ServiceDetail => ({ slug, name, title, metaDescription, h1, intro, bullets, body, faqs });

const FRR: MicroSiteContent = {
  heroH1: "Florida Roof Replacement Done Right",
  heroSubhead:
    "Full tear-off roof replacements built to the latest Florida Building Code — shingle, metal, tile, and flat systems with a lifetime workmanship guarantee.",
  heroBullets: [
    "Free written replacement quotes statewide",
    "Hurricane-rated underlayment and fastening on every roof",
    "Financing as low as $99/mo (subject to credit approval)",
    "Insurance claim assistance with every Florida carrier",
    "Manufacturer-certified GAF, Owens Corning, CertainTeed installers",
    "Lifetime workmanship guarantee on every replacement",
  ],
  servicesH1: "Roof Replacement Services Across Florida",
  servicesIntro:
    "We replace every major Florida roof system — asphalt shingle, standing-seam metal, concrete and clay tile, and low-slope flat membrane. Below is the catalog we replace most often, with what each system costs and how long it lasts in Florida's heat, sun, and wind.",
  servicesOutro:
    "Not sure which system fits your home or budget? Request a free estimate and we'll walk every option with you on-site.",
  pillarBody: [
    "Most Florida roofs come up for replacement somewhere between year 15 and year 25 — sooner on coastal homes, sooner again on roofs that took a hit from a named storm. The replacement window is also when the wind-mitigation form on file with your insurance carrier expires, which means a properly documented replacement can drop your homeowner's premium the same month it's installed.",
    "We replace every major residential and light-commercial roof system used in Florida: architectural and impact-rated shingle, standing-seam and 5V crimp metal, concrete and clay tile (full replacement and lift-and-relay), and TPO/PVC/modified flat-roof sections. Every replacement is a full tear-off — Florida code no longer allows overlay shingle roofs — and every replacement includes deck inspection, rotten plywood replacement, synthetic underlayment, peel-and-stick valleys, and current Florida Building Code fastening.",
    "Use the system catalog below to weigh upfront cost against life, wind rating, and insurance impact. Then request a free written estimate — we'll walk every option that fits your home, your budget, and how long you plan to be in the house.",
  ],
  services: [
    detail(
      "shingle-roof-replacement",
      "Shingle Roof Replacement",
      "Shingle Roof Replacement in Florida | Architectural & Impact-Rated",
      "Architectural and impact-rated shingle roof replacement across Florida. Manufacturer-certified installers, lifetime workmanship guarantee.",
      "Shingle Roof Replacement in Florida",
      "Most Florida homes are shingle, and most shingle replacements happen 18–25 years in. Here's what to expect.",
      [
        "Architectural (dimensional) shingles standard — no 3-tab",
        "Class 4 impact-rated upgrades for insurance discounts",
        "Synthetic underlayment + peel-and-stick valleys",
        "6-nail high-wind fastening pattern",
        "Manufacturer warranties up to 50 years",
        "Fresh wind-mitigation form on completion",
      ],
      [
        "Florida shingle replacements typically run $12,000 to $22,000 for a 20–25 square home, depending on pitch, decking condition, and shingle grade. We tear off to the deck, replace any rotten plywood, fasten per Florida Building Code wind zone, and install a full synthetic underlayment with peel-and-stick at every valley and penetration.",
        "Impact-rated shingles cost roughly $1,500 more on a typical roof but often pay back within a few years through windstorm-discount line items on your homeowner's insurance. We document the upgrade on your fresh wind-mit form so the carrier picks up the credit at your next policy renewal.",
        "Every replacement also includes ridge venting where the existing system is undersized — proper attic ventilation extends shingle life 3-5 years on Florida roofs and is the single most-skipped detail on cheap replacement bids.",
      ],
      [
        { q: "How long does a shingle replacement take?", a: "Most Florida shingle replacements complete in 1–2 days once material is delivered." },
        { q: "Will I get a new wind-mitigation form?", a: "Yes — we document every wind-mit credit (sealed deck, secondary water barrier, fastening pattern) at install." },
        { q: "What about my old satellite dish or solar attic fan?", a: "We dismount and reinstall (or properly remove) every roof-mounted accessory and flash every penetration to current FBC." },
      ],
    ),
    detail(
      "metal-roof-replacement",
      "Metal Roof Replacement",
      "Metal Roof Replacement in Florida | Standing Seam & 5V",
      "Standing seam and 5V crimp metal roof replacement across Florida. Hurricane-rated, lifetime materials.",
      "Metal Roof Replacement in Florida",
      "Replacing an aging shingle or tired metal roof with a new metal system is the longest-lasting move you can make on a Florida home.",
      [
        "24-gauge standing seam — concealed fasteners",
        "5V crimp galvalume — classic Florida exposed-fastener system",
        "Stone-coated steel option for tile-look at metal life",
        "Energy Star reflective finishes — 20–40°F cooler attics",
        "50-year+ paint warranties from major mills",
        "Florida Product Approval / Miami-Dade NOA documented",
      ],
      [
        "Metal replacement on a typical Florida home runs $25,000 to $55,000 depending on system, gauge, and panel profile. Standing seam costs more up front than 5V but eliminates exposed fasteners (the failure point on older metal roofs).",
        "Every metal system we install carries Florida Product Approval and Miami-Dade NOA where applicable. We document the upgrade on a fresh wind-mitigation form so your insurance carrier picks up the discounts immediately — most Florida carriers itemize a meaningful wind-mit credit for a properly installed metal roof.",
        "We install in aluminum on coastal homes (better salt-air corrosion resistance) and steel inland for the better cost-to-strength ratio. Every install includes a high-temperature peel-and-stick underlayment and 1x4 furring strips where the existing deck allows for ventilation.",
      ],
      [
        { q: "How long does a metal roof last in Florida?", a: "Standing seam routinely lasts 50+ years; properly installed 5V crimp 40+ years." },
        { q: "Is metal noisier in rain?", a: "No — modern metal roofs over solid decking with synthetic underlayment are quieter than shingle in most homes." },
        { q: "Can I add solar later?", a: "Yes — standing seam is the easiest residential roof to add solar to thanks to clamp-on rail systems that don't penetrate the panel." },
      ],
    ),
    detail(
      "tile-roof-replacement",
      "Tile Roof Replacement",
      "Tile Roof Replacement in Florida | Concrete & Clay",
      "Concrete and clay tile roof replacement across Florida, with hurricane-rated underlayment built to outlast the tile.",
      "Tile Roof Replacement in Florida",
      "Tile roofs in Florida often fail at the underlayment long before the tile itself does. We replace both right.",
      [
        "Concrete and clay barrel tile — full tear-off or lift-and-relay",
        "Peel-and-stick or two-ply hot-mop underlayment options",
        "Tile color matching where supply allows",
        "Hurricane-rated fastening per current FBC wind zone",
        "Ridge and hip re-cementing to current code",
        "Lead-coated copper flashing options for clay tile",
      ],
      [
        "Tile replacements on a typical Florida home run $30,000 to $65,000 depending on tile profile, underlayment system, and whether we can lift and relay existing tile.",
        "If your tile is intact but underlayment is failing, a lift-and-relay can save 30–40% versus full replacement while resetting the 20–25 year underlayment clock. We test-lift a sample area before quoting — if breakage stays under ~15%, relay is the right call.",
        "Tile is heavy. Before we quote we verify truss capacity against the tile profile you're switching to (Spanish barrel weighs roughly 950 lbs per square; flat concrete tile 600 lbs per square). On homes built for shingle, the upgrade to tile sometimes requires truss reinforcement — we'll catch it on the inspection, not on install day.",
      ],
      [
        { q: "Lift-and-relay or full replacement?", a: "We give you both quotes — relay if tile breakage stays under ~15%, full replacement otherwise." },
        { q: "Can you match my existing tile?", a: "We work with major Florida tile distributors and salvage when needed for matching profiles." },
        { q: "Will my homeowner's insurance cover a tile re-roof?", a: "Carriers cover damage-driven replacement (storm, hail). Age-driven replacement is typically out of pocket but qualifies for the same wind-mit discounts on completion." },
      ],
    ),
    detail(
      "flat-roof-replacement",
      "Flat Roof Replacement",
      "Flat Roof Replacement in Florida | TPO, PVC, Modified Bitumen",
      "Single-ply and modified bitumen flat roof replacement across Florida for residential and commercial flat sections.",
      "Flat Roof Replacement in Florida",
      "Flat sections fail first on most Florida homes — over lanais, additions, or whole low-slope flat-roofed buildings.",
      [
        "TPO and PVC single-ply heat-welded membranes",
        "Modified bitumen torch-down or self-adhered systems",
        "Tapered insulation to fix ponding water",
        "60-mil and 80-mil membrane options",
        "Manufacturer warranties up to 25 years",
        "ENERGY STAR cool-roof reflective finishes",
      ],
      [
        "Flat-section replacements typically run $8 to $15 per square foot depending on system, tear-off complexity, and insulation. We strip to the deck, address any rot, install tapered insulation where ponding exists, and heat-weld every seam on single-ply jobs.",
        "On the typical Florida home, the flat section over the lanai or addition is the first roof element to fail — usually because the original install undersized the slope and water ponds at the parapet. Tapered insulation built into the replacement scope is non-negotiable for us and the single biggest reason our flat-roof replacements outlast the warranty.",
      ],
      [
        { q: "Can you fix ponding water?", a: "Yes — tapered insulation is the standard fix and is included in most of our flat replacement quotes." },
        { q: "TPO or PVC?", a: "PVC if grease/oil/chemical exposure (restaurants, vent locations); TPO otherwise." },
      ],
    ),
    detail(
      "storm-damage-replacement",
      "Storm Damage Replacement",
      "Storm Damage Roof Replacement in Florida | Insurance Claim Help",
      "Storm-damage roof replacement across Florida with full insurance claim documentation and adjuster meetings.",
      "Storm Damage Roof Replacement",
      "Hurricane, hail, or wind event? Here's how we move you from damage to replacement.",
      [
        "Free documented inspection within 24–48 hours",
        "Emergency tarping same-day where needed",
        "We meet your adjuster on-site",
        "Full photo and measurement documentation submitted to carrier",
        "Replacement to spec on approved claims",
        "We only collect from the carrier and your deductible",
      ],
      [
        "Most Florida insurance carriers replace roofs that show wind or hail damage above defined thresholds. We document everything the adjuster needs, fight for full replacement when partial repair won't last, and only collect from the carrier and your deductible.",
        "We never ask homeowners to commit to work before their claim is decided. Our documentation packet (drone imagery, slope-by-slope inspection photos, wind speed correlation, interior leak photos) is what a Florida public adjuster would charge a percentage of the claim to produce — for our customers it's part of the free inspection.",
      ],
      [
        { q: "Will my carrier pay the full replacement?", a: "If wind, hail, or storm damage is documented above your carrier's threshold and your policy has replacement-cost coverage, typically yes." },
        { q: "Do you take assignment of benefits (AOB)?", a: "No — we work for the homeowner, not the carrier. Florida AOB has been a source of consumer abuse and we don't participate." },
        { q: "How fast can you tarp?", a: "Same-day in most of our service area when damage exposes the interior to weather." },
      ],
    ),
    detail(
      "deck-rot-and-structural-repair",
      "Deck Rot & Structural Repair",
      "Roof Deck Rot & Structural Repair in Florida | Plywood, Truss",
      "Deck rot and structural roof repair across Florida — rotten plywood replacement, fascia repair, truss reinforcement.",
      "Roof Deck Rot & Structural Repair",
      "Most Florida re-roofs uncover at least some rotten decking. Here's how we handle it — without surprising you on invoice day.",
      [
        "Itemized $/sheet plywood replacement (no estimates, no surprises)",
        "Fascia and sub-fascia rot repair",
        "Tongue-and-groove decking restoration on cottage roofs",
        "Truss reinforcement and sistering when needed",
        "Structural engineer letters where required by permit",
      ],
      [
        "Every replacement quote we write includes a per-sheet plywood unit price up front — typical Florida range is $85–$135 per sheet installed. You see the actual sheet count after tear-off, before we close in. No surprise invoices.",
        "Fascia and sub-fascia rot is its own line item, typically $12–$18 per linear foot. It's also the single most-skipped item on cheap re-roof bids, which is why those roofs leak at the gutter line within five years.",
      ],
      [
        { q: "How do I know if I have deck rot?", a: "Soft spots underfoot, daylight visible from the attic, or stains on the underside of the decking. The full picture comes after tear-off." },
        { q: "Will deck repair delay the project?", a: "Almost never — we carry plywood on the truck and replace as we go." },
      ],
    ),
    detail(
      "wind-mitigation-replacement",
      "Wind-Mitigation Replacement",
      "Wind-Mitigation Roof Replacement in Florida | Insurance Discount Roofs",
      "Roof replacement engineered to maximize wind-mitigation credits and lower Florida homeowner's insurance premiums.",
      "Wind-Mitigation Replacement",
      "Every wind-mit form line item is a Florida insurance discount. Here's how we build the roof to score full marks.",
      [
        "Secondary water resistance — peel-and-stick over entire deck",
        "Sealed deck attachment per FBC 1518.4",
        "6-nail high-wind shingle fastening pattern",
        "Reinforced gable bracing when structurally needed",
        "Hip-roof geometry preserved (or built where possible)",
        "Florida Product Approved / Miami-Dade NOA materials",
      ],
      [
        "The wind-mitigation form is a checklist of construction details your carrier discounts. A roof built to maximize the form (peel-and-stick over the entire deck, 6-nail pattern, sealed roof-to-wall connections, FBC-approved materials) drops Florida homeowner's premiums by hundreds to thousands per year.",
        "We document every credit at install and hand you a signed wind-mit form for your carrier — no need to schedule and pay for a separate inspector after the project closes.",
      ],
      [
        { q: "What's the most valuable wind-mit upgrade?", a: "Secondary water resistance (peel-and-stick over the deck) and roof-to-wall connection are typically the largest discount line items." },
        { q: "Do you handle the wind-mit inspection?", a: "Yes — included with every replacement we perform." },
      ],
    ),
  ],
  aboutH1: "About Florida Roof Replacement",
  aboutBody: [
    "FloridaRoofReplacement.com is the replacement-focused brand operated by O'Brien Contracting, a fully licensed Florida roofing contractor (License CCC1335947).",
    "Every replacement is installed by O'Brien Contracting in-house crews — never subcontracted day-labor — and backed by our lifetime workmanship guarantee.",
    "We've been replacing Florida roofs across Sarasota, Charlotte, Lee, Collier, Manatee, Miami-Dade, Broward, and Palm Beach counties since the company was founded, and we're licensed statewide.",
    "We're manufacturer-certified by GAF, Owens Corning, and CertainTeed for shingle, and approved installers for every major metal and tile mill operating in Florida. Certification matters because it unlocks the longer-tail manufacturer warranties (up to 50 years on shingle) that uncertified roofers can't offer.",
    "We don't run a sales-floor model with high-pressure quotes. Estimates are written, itemized, and held open 30 days. The roof we quote is the roof we install — no surprise change orders.",
  ],
  faqs: [
    { q: "How long does a roof replacement take?", a: "1–3 days for shingle, 3–7 days for tile and metal, depending on roof size and complexity." },
    { q: "Do you handle insurance claims?", a: "Yes. We document damage, meet your adjuster, and complete approved replacements to spec." },
    { q: "Do you offer financing?", a: "Yes — payments as low as $99/mo subject to credit approval. We disclose terms in writing before you sign." },
    { q: "Are you licensed and insured?", a: "Yes. Licensed in Florida (CCC1335947) with general liability and workers' compensation insurance." },
    { q: "What's your warranty?", a: "Lifetime workmanship guarantee plus the manufacturer's material warranty (up to 50 years on shingle, lifetime on metal)." },
    { q: "Do you pull permits?", a: "Yes — every Florida replacement is permitted, inspected, and closed with the local building department. We never roof under the table." },
    { q: "What happens if rain hits during tear-off?", a: "We dry-in the deck the same day with synthetic underlayment and peel-and-stick. We don't leave open decks overnight." },
    { q: "Can you match my HOA's color and material specs?", a: "Yes — we work with HOA architectural review boards routinely and bring color and profile samples to the quote." },
  ],
  extraTools: ["storm-damage-analyzer"],
};

const FCR: MicroSiteContent = {
  heroH1: "Florida Commercial Roofing — TPO, PVC & Modified Bitumen",
  heroSubhead:
    "Single-ply, modified bitumen, metal, and coating systems for warehouses, retail, multifamily, and HOA-managed buildings across Florida.",
  heroBullets: [
    "TPO, PVC, EPDM single-ply — heat-welded seams",
    "Modified bitumen, BUR, and silicone restoration coatings",
    "Property-manager and HOA preferred — scheduled, clean job sites",
    "Bonded and insured commercial crews statewide",
  ],
  servicesH1: "Commercial Roofing Services Across Florida",
  servicesIntro:
    "Commercial roofs fail differently than residential. They pond, they leak at penetrations, they age at seams. Here are the systems we install, recover, and restore on Florida commercial buildings.",
  servicesOutro:
    "Need a written budget for next fiscal year or an immediate replacement quote? Request an estimate and we'll have it back within five business days.",
  pillarBody: [
    "Florida commercial roofs operate in the harshest membrane environment in the United States: 95°F+ rooftop temperatures, salt-air corrosion within ten miles of either coast, and hurricane-force uplift events every summer. The system you specify determines whether the building is dry for fifteen years or twenty-five.",
    "We install and service every major low-slope and steep-slope commercial system used in Florida. TPO and PVC dominate new construction because they're white-reflective, heat-weldable, and code-compliant for FBC HVHZ zones. Modified bitumen is still the workhorse for re-cover over existing built-up roofs. Standing-seam metal is the long-term answer for steep-slope commercial. Silicone coatings extend a sound roof another decade without a tear-off.",
    "Use the comparison table below to triage what fits your building's slope, budget, and ownership horizon, then click into each system page for installation detail, manufacturer options, and Florida-specific code notes (FBC 1504, ASCE 7 wind zones, HVHZ requirements).",
  ],
  services: [
    detail(
      "tpo-roofing",
      "TPO Roofing",
      "Commercial TPO Roofing in Florida | White Reflective Membrane",
      "Heat-welded TPO single-ply commercial roofing across Florida. 60-mil and 80-mil systems, 20–25 year warranties.",
      "TPO Roofing in Florida",
      "The most-installed commercial flat roof in Florida — white, reflective, and warrantied.",
      [
        "60-mil and 80-mil reinforced membranes",
        "Heat-welded seams (not glued) for permanent watertight bonds",
        "Cool-roof reflective — meaningful cooling-cost reduction",
        "Manufacturer warranties up to 25 years",
      ],
      [
        "TPO runs $7–$12 per square foot installed depending on insulation, tear-off, and warranty length. White TPO can reduce roof-surface temperatures by 50°F+ compared to dark BUR or modified, which translates into measurable HVAC savings on Florida buildings.",
      ],
      [
        { q: "60-mil or 80-mil?", a: "80-mil is the right call for buildings with heavy foot traffic, hail exposure, or longer warranty needs." },
      ],
    ),
    detail(
      "pvc-roofing",
      "PVC Roofing",
      "Commercial PVC Roofing in Florida | Chemical-Resistant Membrane",
      "Heat-welded PVC commercial roofing across Florida — best for restaurants, food processing, and chemical-exposure buildings.",
      "PVC Roofing in Florida",
      "PVC is TPO's chemical-resistant cousin — the right call when grease, oils, or chemicals will hit the roof.",
      [
        "Heat-welded, chemical-resistant membrane",
        "Best choice for restaurants, food processing, manufacturing",
        "Fire-rated and wind-rated for Florida codes",
        "Manufacturer warranties up to 25 years",
      ],
      [
        "PVC runs slightly more than TPO ($8–$14 installed) and is the right call any time the building exhausts grease, oil, or solvents onto the roof.",
      ],
      [
        { q: "TPO vs PVC for a restaurant?", a: "PVC, every time. TPO degrades from kitchen exhaust." },
      ],
    ),
    detail(
      "modified-bitumen-roofing",
      "Modified Bitumen Roofing",
      "Commercial Modified Bitumen Roofing in Florida | SBS & APP",
      "Multi-ply modified bitumen commercial roofing across Florida. Torch-applied or self-adhered systems.",
      "Modified Bitumen Roofing in Florida",
      "Multi-ply asphalt-based systems for flat commercial roofs needing redundancy and durability.",
      [
        "SBS and APP modified systems",
        "Torch-applied or self-adhered base + cap sheet",
        "Granulated cap sheets in multiple colors",
        "20-year warranties standard",
      ],
      [
        "Modified bitumen runs $6–$10 per square foot installed and shines on roofs with high foot traffic or HVAC density. Multi-ply construction means a puncture in one ply rarely turns into a leak.",
      ],
      [
        { q: "Modified vs TPO?", a: "Modified for foot traffic and HVAC-dense roofs; TPO for energy savings and warranty length." },
      ],
    ),
    detail(
      "silicone-roof-coatings",
      "Silicone Roof Coatings",
      "Commercial Silicone Roof Coatings in Florida | Restoration",
      "Silicone roof coating restoration for Florida commercial buildings. Extend roof life 10–20 years at 40–70% of replacement cost.",
      "Silicone Roof Coatings",
      "Restoration coatings are the best-kept secret in commercial roofing — when the substrate is sound.",
      [
        "Single-coat silicone over prepared substrate",
        "Adds 10–20 years to a sound roof",
        "Ponding-water rated (where most coatings fail)",
        "40–70% of replacement cost",
      ],
      [
        "Silicone coatings run $3–$6 per square foot installed and are the right move on flat commercial roofs where the substrate is dry, sound, and just aging out.",
      ],
      [
        { q: "Will coating fix a leak?", a: "Coating seals leak paths once we've addressed the source — wet insulation has to come out first." },
      ],
    ),
    detail(
      "commercial-metal-roofing",
      "Commercial Metal Roofing",
      "Commercial Metal Roofing in Florida | Standing Seam Systems",
      "Standing seam and structural metal panel commercial roofing across Florida. 24-gauge systems with 50-year paint warranties.",
      "Commercial Metal Roofing",
      "For sloped commercial buildings — warehouses, equestrian, agricultural — metal wins on life-cycle cost.",
      [
        "24-gauge standing seam panels",
        "Structural and architectural panel systems",
        "Florida Product Approval and Miami-Dade NOA",
        "50-year+ paint warranties",
      ],
      [
        "Commercial metal runs $9–$16 per square foot installed depending on panel, profile, and insulation package. Most metal commercial roofs outlive two membrane replacements on the same building.",
      ],
      [
        { q: "Recover or replace?", a: "Metal-over-metal recovers are possible when the substrate is sound and the existing fastener pattern is intact." },
      ],
    ),
  ],
  aboutH1: "About Florida Commercial Roofing",
  aboutBody: [
    "Florida-Commercial-Roofing.com is the commercial-focused brand operated by O'Brien Contracting, a fully licensed Florida roofing contractor (License CCC1335947).",
    "We work with Florida property managers, HOA boards, REIT asset managers, retail owners, and industrial owners on roofs from 5,000 SF strip centers to 500,000 SF distribution centers.",
    "Every commercial bid includes a written scope, written warranty, and named project manager. We don't subcontract out crews to day-labor firms.",
  ],
  faqs: [
    { q: "Do you bond commercial work?", a: "Yes — bonded and insured for commercial projects in Florida." },
    { q: "Do you handle multi-property contracts?", a: "Yes. We work with property managers running 10+ buildings across Florida." },
    { q: "Recover vs replace?", a: "Recover when the substrate is sound and dry; replace when wet insulation or substrate damage is documented." },
    { q: "Do you provide capital planning budgets?", a: "Yes — written 5- and 10-year roof condition assessments and budget recommendations are part of our work." },
    { q: "What systems do you warranty?", a: "All major manufacturers — Carlisle, GAF, Firestone, Johns Manville, Sika, Versico, and more." },
  ],
  extraTools: [],
};

const FTR: MicroSiteContent = {
  heroH1: "Florida Tile Roofing — Concrete & Clay Specialists",
  heroSubhead:
    "Concrete and clay barrel tile installs, repairs, and re-roofs built for Florida hurricane wind zones — with underlayment systems engineered to outlast the tile.",
  heroBullets: [
    "Concrete, clay, and flat tile — repair, re-roof, full replacement",
    "Peel-and-stick or two-ply hot-mop underlayment options",
    "Tile matching for partial repairs and storm damage",
    "Lift-and-relay specialists — save 30–40% vs full replacement",
    "Hurricane-rated fastening per current FBC wind zone",
    "Florida-licensed contractor (CCC1335947), bonded and insured",
  ],
  servicesH1: "Tile Roofing Services Across Florida",
  servicesIntro:
    "Tile is the longest-lasting residential roof system in Florida — done right. Here are the tile services we specialize in, with what each costs and when each is the right call.",
  servicesOutro:
    "Tile work is specialist work. Request a free on-site inspection and we'll tell you whether you need repair, relay, or full replacement.",
  pillarBody: [
    "Tile is the most permanent residential roof in Florida — concrete tile lasts 50+ years, clay barrel tile 75+. But the underlayment beneath the tile is asphalt-based and lives 20-25 years in Florida heat. That mismatch is why most tile work in Florida is underlayment work: the tile is fine, the deck protection underneath is failing.",
    "We're one of the few Florida roofers running dedicated tile crews. Tile work — lift-and-relay, profile matching, ridge and hip cementing, hurricane-rated fastening — is its own discipline, and the failure modes (slipped tile at the rake, cracked ridge cement, valley underlayment punctures from foot traffic) require tile-specific repair patterns that a shingle crew can't deliver.",
    "Use the catalog below to figure out what your tile roof actually needs: a single-tile repair, an underlayment lift-and-relay, or a full tear-off and replacement. Every quote we write is itemized, written, and held open 30 days — no high-pressure tile-replacement upsells.",
  ],
  services: [
    detail(
      "concrete-tile-roofing",
      "Concrete Tile Roofing",
      "Concrete Tile Roofing in Florida | Barrel & Flat Profiles",
      "Concrete tile roofing across Florida — barrel and flat profiles, 50+ year life, hurricane-rated fastening.",
      "Concrete Tile Roofing in Florida",
      "Concrete tile is the most-installed tile in Florida — durable, affordable, and available in every profile.",
      [
        "Eagle, US Tile, Boral, and other major mills",
        "Barrel, flat, and S-profile shapes",
        "Color-through and surface-finish options",
        "50+ year service life",
        "Hurricane-rated fastening systems",
        "Florida Product Approved across major mills",
      ],
      [
        "Concrete tile installs run $18–$28 per square foot installed on a typical Florida home depending on profile, color, and roof complexity. The tile itself routinely outlasts the underlayment beneath it by 2–3x — which is why most tile work in Florida is underlayment work.",
        "Concrete is the right call when you want the tile look at a meaningful discount to clay, or when your HOA spec calls for concrete specifically. Color-through concrete (color baked into the body of the tile, not just the surface) is what we recommend for full-sun Florida exposures.",
      ],
      [
        { q: "Concrete vs clay tile?", a: "Concrete is less expensive and offered in more profiles; clay carries the longest life and the classic terracotta look." },
        { q: "How heavy is concrete tile?", a: "Flat concrete tile runs around 600 lbs per square; barrel concrete around 950. Truss capacity matters — we verify before quoting." },
        { q: "Does the color fade?", a: "Surface-finish concrete fades faster than color-through. We recommend color-through tile on full-sun exposures." },
      ],
    ),
    detail(
      "clay-tile-roofing",
      "Clay Tile Roofing",
      "Clay Tile Roofing in Florida | Spanish, Mission, Mediterranean",
      "Clay tile roofing across Florida — terracotta, Spanish, mission, and Mediterranean barrel profiles built for hurricane wind zones.",
      "Clay Tile Roofing in Florida",
      "The classic Florida roof — terracotta clay, 75+ year life, and the only roof that looks better as it ages.",
      [
        "Spanish, mission, and Mediterranean profiles",
        "Natural terracotta and glazed color options",
        "75+ year service life",
        "Premium ridge and rake tile profiles",
        "Lead-coated copper flashing options",
        "Specified by Florida coastal HOAs and historic districts",
      ],
      [
        "Clay tile installs run $25–$40 per square foot installed on a typical Florida home depending on profile, glazing, and roof complexity. Higher up front than concrete, but clay outlasts concrete by decades.",
        "Clay is required spec in many Florida coastal HOAs and historic districts (Coral Gables, Sarasota's John Ringling district, and most Palm Beach barrier islands). We work directly with architectural review boards and bring color and profile samples to every quote.",
      ],
      [
        { q: "Why does clay cost more than concrete?", a: "Clay is fired ceramic — heavier production process, longer life, and the look most South Florida homes are built for." },
        { q: "Glazed or natural clay?", a: "Glazed holds color longer and resists algae. Natural ages into a patina that many Florida homeowners prefer." },
      ],
    ),
    detail(
      "tile-roof-repair",
      "Tile Roof Repair",
      "Tile Roof Repair in Florida | Cracked Tile & Underlayment Leaks",
      "Tile roof repair across Florida — cracked tile replacement, underlayment patching, valley and flashing repair.",
      "Tile Roof Repair",
      "Broken tile, leak at the valley, or active drip in the ceiling? Tile repair is its own discipline — don't let a shingle crew touch it.",
      [
        "Single-tile replacement with profile matching",
        "Underlayment patching at penetrations and valleys",
        "Ridge and hip re-cementing",
        "Flashing repair and replacement",
        "Slipped-tile reset at rakes and eaves",
        "Photographed before-and-after documentation",
      ],
      [
        "Most tile repairs run $400–$2,500. We walk the roof, find every cracked tile and underlayment failure (not just the one over the stain on your ceiling), and quote the full repair in writing.",
        "Common Florida tile failure modes: cracked tiles from solar-panel installers walking the deck, slipped tile at the rake from a hurricane, valley underlayment punctures from satellite-dish removal, and failed ridge cement after 15+ years of UV. Each has a specific repair pattern.",
      ],
      [
        { q: "Can you find tile to match my profile?", a: "Yes — we source from Florida distributors and salvage yards for discontinued profiles." },
        { q: "Will you walk on my tile roof?", a: "Yes, on padded boots and only where the tile is supported. Tile-trained crews break a fraction of what shingle crews do." },
      ],
    ),
    detail(
      "tile-roof-replacement",
      "Tile Roof Replacement",
      "Tile Roof Replacement in Florida | Full Tear-Off",
      "Full tile roof replacement across Florida — concrete and clay tile, hurricane-rated underlayment systems.",
      "Tile Roof Replacement in Florida",
      "When repair won't last or the tile itself is failing, full replacement resets the clock to 50+ years.",
      [
        "Full tear-off to the deck",
        "Peel-and-stick or two-ply hot-mop underlayment",
        "Concrete or clay tile to your specification",
        "Hurricane-rated fastening per current FBC",
        "Fresh wind-mitigation form on completion",
        "Itemized per-sheet decking unit price",
      ],
      [
        "Tile replacements on a typical Florida home run $30,000 to $65,000 depending on tile profile, underlayment system, and roof complexity.",
        "Every tile replacement we write is a fixed-price quote with an itemized per-sheet decking unit price. After tear-off you see exactly how much rotten deck we found — no surprise invoices, no change orders on close-in.",
      ],
      [
        { q: "Do you replace the deck?", a: "Only the rotten sections. We document every sheet we replace with photos." },
        { q: "Truss capacity check?", a: "Yes — required when switching to a heavier tile profile, and we catch it on the inspection, not on install day." },
      ],
    ),
    detail(
      "tile-lift-and-relay",
      "Tile Lift-and-Relay",
      "Tile Lift-and-Relay in Florida | Underlayment Replacement",
      "Tile lift-and-relay underlayment replacement across Florida — save 30–40% versus full tile replacement when tiles are intact.",
      "Tile Lift-and-Relay",
      "If your tile is sound but the underlayment beneath it is failing, lift-and-relay is the right call.",
      [
        "Lift, stage, and protect existing tile",
        "Tear off and replace underlayment to current FBC",
        "Relay original tile with replacement of cracked pieces",
        "30–40% savings versus full replacement",
        "Resets 20–25 year underlayment clock",
        "Same wind-mit credits as a full replacement",
      ],
      [
        "Lift-and-relay typically runs $18,000–$35,000 — significantly less than full replacement and resetting the 20–25 year underlayment clock. The math works when tile breakage during lifting stays under ~15%.",
        "Lift-and-relay also gets you the same wind-mitigation credits as a full replacement (sealed deck, secondary water resistance, current-code fastening) at a meaningfully lower cost.",
      ],
      [
        { q: "How do you know if relay will work?", a: "We test-lift a sample area before quoting. If too many tiles crack on lift, we recommend full replacement." },
        { q: "Where do you stage the lifted tile?", a: "On the deck where weight is supported, on plywood pads. Never on landscaping or vehicles." },
      ],
    ),
    detail(
      "tile-roof-cleaning",
      "Tile Roof Cleaning & Algae Treatment",
      "Tile Roof Cleaning & Algae Treatment in Florida",
      "Soft-wash tile roof cleaning and algae treatment across Florida — restore curb appeal without high-pressure tile damage.",
      "Tile Roof Cleaning & Algae Treatment",
      "Black streaks, green algae, or lichen on your tile? Soft-wash treatment restores curb appeal without damaging the tile or underlayment.",
      [
        "Soft-wash (low-pressure) chemistry only",
        "Algae, mildew, and lichen treatment",
        "Tile inspection paired with cleaning",
        "Roof-safe fall protection and tile-walk paths",
        "Most homes $400–$1,200",
      ],
      [
        "We never use high-pressure washing on tile — high pressure strips the surface finish on concrete and cracks ridge cement on clay. Soft-wash chemistry kills the biological growth, then is rinsed off at safe pressure.",
        "Every cleaning is paired with a free roof inspection. If we find cracked tile, slipped tile, or underlayment issues, we document them with photos and quote the repair separately.",
      ],
      [
        { q: "Will cleaning damage my tile?", a: "Not with soft-wash. High-pressure cleaning absolutely will." },
        { q: "How long until algae comes back?", a: "Typically 3-5 years in Florida humidity. We can apply a longer-lasting algae-inhibiting treatment as part of the service." },
      ],
    ),
    detail(
      "storm-damaged-tile",
      "Storm-Damaged Tile Repair",
      "Storm-Damaged Tile Roof Repair in Florida | Insurance Claim Help",
      "Storm-damaged tile roof repair across Florida with full insurance claim documentation and adjuster meetings.",
      "Storm-Damaged Tile Repair",
      "Hurricane or wind event broke tile, slipped your ridge, or punctured your underlayment? We document for insurance and repair to spec.",
      [
        "Free documented inspection within 24–48 hours",
        "Emergency tarping over exposed underlayment",
        "We meet your adjuster on-site",
        "Full slope-by-slope photo and measurement packet",
        "Repair or replacement to carrier spec",
        "Tile-profile matching from salvage when needed",
      ],
      [
        "Tile damage from named storms is among the most-disputed claim categories in Florida — carriers routinely deny full replacement when partial repair will hold. We document every cracked tile, every slipped course, every underlayment puncture, and submit the full packet to the adjuster.",
        "When repair won't last because cracked tile is distributed across the roof (not just one slope), we make the case for full replacement and back it with measurement and photo evidence the adjuster needs to authorize it.",
      ],
      [
        { q: "Can you tarp a tile roof?", a: "Yes — over the underlayment after we remove damaged tile, with breathable mesh tarps that don't trap moisture against the deck." },
        { q: "What if the claim is denied?", a: "We help you supplement with additional documentation when warranted, and recommend a public adjuster when the carrier won't engage in good faith." },
      ],
    ),
  ],
  aboutH1: "About Florida Tile Roofing",
  aboutBody: [
    "Florida-Tile-Roofing.com is the tile-specialty brand operated by O'Brien Contracting, a fully licensed Florida roofing contractor (License CCC1335947).",
    "Tile is its own discipline — and most Florida roofers do it poorly. We've built dedicated tile crews trained on lift-and-relay, profile matching, and hurricane-rated fastening systems.",
    "Every tile job is backed by our lifetime workmanship guarantee plus the manufacturer's material warranty.",
    "We work with Florida coastal HOAs, historic districts, and architectural review boards on color, profile, and material spec — bringing samples to every quote and routing approvals before tear-off.",
    "All work is permitted, inspected, and closed through the local building department. We pull every permit in our name and never ask homeowners to permit work themselves.",
  ],
  faqs: [
    { q: "How long does a tile roof last in Florida?", a: "Concrete and clay tile routinely last 50+ years. The underlayment beneath typically needs replacement at 20–25 years." },
    { q: "Can you match my existing tile?", a: "Often yes — we work with major Florida distributors and salvage yards for discontinued profiles." },
    { q: "Lift-and-relay or full replacement?", a: "Relay if tile breakage stays under ~15% on a test lift; full replacement otherwise." },
    { q: "Do you handle storm-damaged tile?", a: "Yes — including documenting the damage for your insurance carrier." },
    { q: "Are tile roofs hurricane-rated?", a: "Yes, when installed with current Florida Building Code fastening systems." },
    { q: "Can I walk on my tile roof?", a: "Only along supported tile-walk paths and in padded boots. Most homeowners shouldn't — we'll do free annual inspections instead." },
    { q: "Will a heavier tile profile require truss reinforcement?", a: "Sometimes — we verify truss capacity against the new tile weight before quoting and write reinforcement into the scope when needed." },
    { q: "Do you work with HOAs and historic boards?", a: "Yes, routinely. We bring color and profile samples, route material approvals, and align permit timing with HOA review cycles." },
  ],
  extraTools: ["roof-visualizer"],
};

const FSR: MicroSiteContent = {
  heroH1: "Florida Shingle Roofing — Architectural & Impact-Rated",
  heroSubhead:
    "Architectural and impact-rated asphalt shingle installs and replacements built to current Florida Building Code. Manufacturer-certified installers.",
  heroBullets: [
    "GAF, Owens Corning, and CertainTeed architectural shingles",
    "Class 4 impact-rated options for insurance discounts",
    "Synthetic underlayment + peel-and-stick valleys standard",
    "Manufacturer warranties up to 50 years",
  ],
  servicesH1: "Shingle Roofing Services Across Florida",
  servicesIntro:
    "Shingle is the most-installed Florida roof. Here are the shingle services we specialize in.",
  servicesOutro:
    "Ready for a free written shingle quote? Most quotes are scheduled within two business days.",
  pillarBody: [
    "Shingles are the most-installed Florida roof system — affordable, fast to install, and now available in impact-rated Class 4 versions that qualify for homeowner's-insurance wind-mit discounts in most Florida carriers. But Florida shingles also fail faster than the national average: UV, salt air, and named-storm uplift cycle them hard.",
    "The difference between a fifteen-year shingle and a thirty-year shingle in Florida isn't the brand alone — it's the underlayment system, the fastening pattern, and the ridge/eave/valley details. We install GAF, Owens Corning, and CertainTeed architectural and impact-rated systems with synthetic underlayment, peel-and-stick valleys, and 6-nail high-wind fastening on every roof.",
    "Use the table to compare lifespan, cost band, and wind rating across our most-installed shingle systems, then drill into each system page for code notes (FBC 1507.2), manufacturer warranty terms, and what to expect during a Florida shingle install.",
  ],
  services: [
    detail(
      "architectural-shingle-roofing",
      "Architectural Shingle Roofing",
      "Architectural Shingle Roofing in Florida | Dimensional Shingles",
      "Architectural (dimensional) shingle roofing across Florida — GAF, Owens Corning, CertainTeed installations.",
      "Architectural Shingle Roofing",
      "The Florida shingle standard — dimensional, wind-rated, and warrantied 30–50 years.",
      [
        "GAF Timberline HDZ, Owens Corning Duration, CertainTeed Landmark",
        "Wind-rated to 130 MPH (Florida Product Approved)",
        "Algae-resistant granules",
        "30–50 year manufacturer warranties",
      ],
      [
        "Architectural shingles run $14–$22 per square foot installed on a typical Florida home — including synthetic underlayment, peel-and-stick valleys, and full FBC fastening.",
      ],
      [
        { q: "Architectural vs 3-tab?", a: "We don't install 3-tab on Florida homes — wind ratings, life, and warranty are all worse." },
      ],
    ),
    detail(
      "impact-rated-shingle-roofing",
      "Impact-Rated Shingle Roofing",
      "Impact-Rated Shingle Roofing in Florida | Class 4 Hail-Rated",
      "Class 4 impact-rated shingle roofing across Florida — qualifies for windstorm insurance discounts in most Florida counties.",
      "Impact-Rated (Class 4) Shingle Roofing",
      "The shingle upgrade that pays for itself — Class 4 impact rating unlocks insurance discounts in most Florida counties.",
      [
        "UL 2218 Class 4 impact rating",
        "Qualifies for windstorm discounts in most FL counties",
        "Hail and debris resistant",
        "$1,500–$2,500 typical upgrade over architectural",
      ],
      [
        "Class 4 impact-rated shingles cost roughly $1,500–$2,500 more on a typical Florida home but routinely pay back in 3–6 years through line-item insurance discounts. We document the upgrade on your fresh wind-mitigation form so the discount lands the day your policy renews.",
      ],
      [
        { q: "Is the discount real?", a: "Yes — most Florida carriers itemize a Class 4 line credit on the wind-mit form." },
      ],
    ),
    detail(
      "shingle-roof-replacement",
      "Shingle Roof Replacement",
      "Shingle Roof Replacement in Florida | Full Tear-Off",
      "Full shingle roof replacement across Florida — tear-off, deck replacement, synthetic underlayment, current-code fastening.",
      "Shingle Roof Replacement",
      "Replace your Florida shingle roof to current FBC and reset the wind-mit clock.",
      [
        "Full tear-off — no overlay roofs in Florida",
        "Rotten deck replacement included as needed",
        "Full synthetic underlayment + peel-and-stick valleys",
        "Current Florida Building Code fastening pattern",
      ],
      [
        "Shingle replacements run $12,000–$22,000 on a typical Florida home depending on size, pitch, and decking condition. Every replacement comes with a fresh wind-mitigation form documenting every credit (sealed deck, secondary water barrier, fastening pattern).",
      ],
      [
        { q: "Can I overlay instead of tear-off?", a: "No — overlay shingle roofs aren't permitted in Florida. Full tear-off only." },
      ],
    ),
    detail(
      "shingle-roof-repair",
      "Shingle Roof Repair",
      "Shingle Roof Repair in Florida | Leak, Wind Damage, Missing Shingles",
      "Shingle roof repair across Florida — wind-damaged shingles, leaks at penetrations, valley repair.",
      "Shingle Roof Repair",
      "Wind ripped shingles, leak at the pipe boot, or missing tabs after a storm? Most shingle repairs are same-week scheduling.",
      [
        "Wind-damaged shingle replacement (color matching)",
        "Pipe boot, valley, and flashing leak repair",
        "Ridge cap replacement",
        "Most repairs $300–$1,500",
      ],
      [
        "We photograph every repair before and after and provide a written summary so you have it on file for any future insurance claim.",
      ],
      [
        { q: "Can you match my existing shingle color?", a: "We try — color match on aged shingles is imperfect, but we get as close as the manufacturer's current run allows." },
      ],
    ),
    detail(
      "shingle-vs-metal-roofing",
      "Shingle vs Metal Roofing in Florida",
      "Shingle vs Metal Roofing in Florida | Cost, Life, Insurance Comparison",
      "Shingle versus metal roofing on a Florida home — costs, lifespan, insurance discounts, and which is right for your house.",
      "Shingle vs Metal Roofing in Florida",
      "The most common question we answer on Florida estimates. Here's the honest comparison.",
      [
        "Shingle: $12K–$22K, 20–25 year life",
        "Metal: $25K–$55K, 50+ year life",
        "Metal qualifies for larger wind-mit discounts",
        "Resale: metal adds more to home value in most FL markets",
      ],
      [
        "If you plan to keep the home 10+ years, metal almost always wins on life-cycle cost. If you're selling in 5 years, shingle keeps more cash in your pocket today.",
      ],
      [
        { q: "Which is better for resale?", a: "Metal in higher-end Florida markets; shingle in entry and mid-market." },
      ],
    ),
  ],
  aboutH1: "About Florida Shingle Roofing",
  aboutBody: [
    "Florida-Shingle-Roofing.com is the shingle-specialty brand operated by O'Brien Contracting, a fully licensed Florida roofing contractor (License CCC1335947).",
    "We are GAF-, Owens-Corning-, and CertainTeed-certified installers — which means we can offer the longer-tail manufacturer warranties (up to 50 years) that uncertified roofers can't.",
    "Every shingle job is backed by our lifetime workmanship guarantee.",
  ],
  faqs: [
    { q: "How long do shingles last in Florida?", a: "Architectural shingles typically last 20–25 years in Florida with proper ventilation. Coastal and full-sun exposure shorten this range." },
    { q: "Are impact-rated shingles worth it?", a: "Yes in most Florida counties — the windstorm discount line item typically pays back the upgrade in 3–6 years." },
    { q: "Do you overlay shingle roofs?", a: "No — Florida code requires full tear-off." },
    { q: "Do you handle storm damage claims?", a: "Yes. We document damage, meet adjusters, and complete approved replacements to spec." },
    { q: "Are you manufacturer-certified?", a: "Yes — GAF, Owens Corning, and CertainTeed." },
  ],
  extraTools: ["roof-visualizer"],
};

const FMR: MicroSiteContent = {
  heroH1: "Florida Metal Roofing — Standing Seam & 5V Specialists",
  heroSubhead:
    "Standing seam, 5V crimp, and stone-coated steel metal roofing engineered for Florida hurricane wind zones. Lifetime materials, lifetime workmanship.",
  heroBullets: [
    "24-gauge standing seam — concealed fasteners",
    "5V crimp galvalume — Florida coastal classic",
    "Energy Star reflective finishes lower attic temps 20–40°F",
    "50-year+ paint warranties from major manufacturers",
  ],
  servicesH1: "Metal Roofing Services Across Florida",
  servicesIntro:
    "Metal is the longest-lasting Florida roof system — when installed right. Here are the metal services we specialize in.",
  servicesOutro:
    "Considering metal but unsure which system fits your home? Request a free on-site quote with both options written out.",
  pillarBody: [
    "Metal is the longest-lasting Florida roof. A properly installed 24-gauge standing-seam roof carries a 50-year material warranty and routinely outlives the home it sits on. 5V crimp galvalume — the classic Florida coastal metal — has protected Keys and Gulf-Coast homes since the 1940s and is still the most cost-effective metal option for traditional architecture.",
    "Metal also performs the best of any system under named-storm wind uplift. Standing-seam panels with hidden clips and stitched seams are rated to 180+ mph; 5V crimp panels with proper screw patterns hold to 150 mph. Reflective Energy Star finishes drop attic temperatures 20-40°F, cutting cooling costs through every Florida summer.",
    "The trade-off is upfront cost: metal runs roughly 1.8-2.5x the cost of shingle per square. Use the comparison table to weigh lifespan and wind rating against budget, then read each system page for installation detail, manufacturer options, and Florida code notes (FBC 1507.4).",
  ],
  services: [
    detail(
      "standing-seam-metal-roofing",
      "Standing Seam Metal Roofing",
      "Standing Seam Metal Roofing in Florida | 24-Gauge Concealed Fastener",
      "24-gauge standing seam metal roofing across Florida — concealed fastener systems with 50-year paint warranties.",
      "Standing Seam Metal Roofing",
      "The premium Florida metal roof — concealed fasteners, panel locks, and 50+ year life.",
      [
        "24-gauge steel and aluminum panels",
        "Mechanically seamed or snap-lock panel profiles",
        "No exposed fasteners — the longest-lasting metal system",
        "50-year paint warranties (Kynar 500 finishes)",
      ],
      [
        "Standing seam installs run $25–$45 per square foot on a typical Florida home depending on gauge, panel width, and seam type. The premium over 5V buys you no exposed fasteners (the weak point on every other metal system) and the longest service life of any residential roof.",
      ],
      [
        { q: "Steel or aluminum panels?", a: "Aluminum on coastal homes (salt air); steel elsewhere for the better strength-to-cost ratio." },
      ],
    ),
    detail(
      "5v-crimp-metal-roofing",
      "5V Crimp Metal Roofing",
      "5V Crimp Metal Roofing in Florida | Galvalume Exposed Fastener",
      "5V crimp galvalume metal roofing across Florida — the classic Florida coastal metal roof.",
      "5V Crimp Metal Roofing",
      "The classic Florida metal roof — galvalume panels, exposed fasteners, 40+ year life.",
      [
        "26-gauge galvalume panels (standard)",
        "Exposed gasketed fastener system",
        "Florida coastal classic — paired with cottages, farmhouses, vernacular homes",
        "30–40 year paint warranties",
      ],
      [
        "5V installs run $14–$22 per square foot on a typical Florida home. The most affordable Florida metal roof and the right look for cottages, farmhouses, and historic-style homes.",
      ],
      [
        { q: "5V vs standing seam life?", a: "5V: 40+ years (fasteners are the limit). Standing seam: 50+ years." },
      ],
    ),
    detail(
      "metal-roof-replacement",
      "Metal Roof Replacement",
      "Metal Roof Replacement in Florida | Standing Seam, 5V, Stone-Coated",
      "Full metal roof replacement across Florida — standing seam, 5V, and stone-coated steel systems.",
      "Metal Roof Replacement",
      "Replacing an aging shingle, tile, or tired metal roof with a new metal system is the longest-lasting move on a Florida home.",
      [
        "Standing seam, 5V, or stone-coated steel",
        "Full tear-off and deck inspection",
        "Hurricane-rated fastening per current FBC",
        "Fresh wind-mit form documenting credits",
      ],
      [
        "Metal replacement runs $25,000–$55,000 on a typical Florida home. Most metal roofs we install will outlast the homeowner.",
      ],
      [
        { q: "Can I install metal over existing shingles?", a: "Metal-over-shingle is allowed in some Florida jurisdictions but not all. We confirm with your county and write up either option." },
      ],
    ),
    detail(
      "metal-roof-repair",
      "Metal Roof Repair",
      "Metal Roof Repair in Florida | Leak, Fastener, Panel Repair",
      "Metal roof repair across Florida — fastener replacement, panel repair, seam re-sealing.",
      "Metal Roof Repair",
      "Leaking at a fastener, lifted panel after a storm, or seam failure? Metal repair is specialist work.",
      [
        "Failed fastener replacement (most common 5V leak)",
        "Panel repair and full-panel replacement",
        "Seam re-sealing on standing seam systems",
        "Flashing and penetration repair",
      ],
      [
        "Most metal repairs run $400–$3,000. We walk the roof, identify every failure mode (not just the one above the stain), and quote in writing.",
      ],
      [
        { q: "How long does a metal repair last?", a: "Indefinitely when the root cause (rusted fastener, failed gasket, panel hole) is properly addressed." },
      ],
    ),
    detail(
      "metal-vs-shingle-roofing",
      "Metal vs Shingle Roofing in Florida",
      "Metal vs Shingle Roofing in Florida | Cost, Life, Insurance Comparison",
      "Metal versus shingle roofing on a Florida home — costs, lifespan, insurance discounts, and which is right for your house.",
      "Metal vs Shingle Roofing in Florida",
      "The other most-asked question on Florida estimates. Here's the honest comparison.",
      [
        "Metal: $25K–$55K, 50+ year life",
        "Shingle: $12K–$22K, 20–25 year life",
        "Metal qualifies for larger wind-mit discounts",
        "Metal often boosts resale in Florida coastal markets",
      ],
      [
        "If you keep the home 10+ years, metal almost always wins on life-cycle cost. If you're selling in 5, shingle keeps more cash in your pocket today.",
      ],
      [
        { q: "Is metal louder in rain?", a: "Modern metal-over-deck systems are quieter than shingle in most homes." },
      ],
    ),
  ],
  aboutH1: "About Florida Metal Roofing",
  aboutBody: [
    "Florida-Metal-Roofing.com is the metal-specialty brand operated by O'Brien Contracting, a fully licensed Florida roofing contractor (License CCC1335947).",
    "Metal is its own discipline. We've built dedicated metal crews trained on standing seam panel seaming, 5V layout, and hurricane-rated fastening systems.",
    "Every metal job is backed by our lifetime workmanship guarantee plus the manufacturer's paint and material warranties.",
  ],
  faqs: [
    { q: "How long does a metal roof last in Florida?", a: "Standing seam routinely 50+ years; properly installed 5V crimp 40+ years." },
    { q: "Are metal roofs hurricane-rated?", a: "Yes — our metal systems carry Florida Product Approval and Miami-Dade NOA where applicable." },
    { q: "Does metal lower my insurance?", a: "In most Florida counties, metal qualifies for wind-mitigation premium reductions. We document the credits on a fresh wind-mit form." },
    { q: "Will a metal roof make my house hotter?", a: "Reflective finishes typically drop attic temperatures 20–40°F vs dark shingle." },
    { q: "Steel or aluminum?", a: "Aluminum on coastal homes; steel inland for the better cost-to-strength ratio." },
  ],
  extraTools: ["roof-visualizer"],
};

const CK: MicroSiteContent = {
  heroH1: "Roof Coatings & Restoration — Coating Kingz",
  heroSubhead:
    "Silicone, acrylic, and elastomeric roof coating systems for flat, low-slope, and metal roofs. Extend roof life 10–20 years at a fraction of replacement cost.",
  heroBullets: [
    "Silicone coatings for ponding-water flat roofs",
    "Elastomeric coatings for metal, EPDM, and modified bitumen",
    "Cool-roof reflective systems lower energy costs 20–40%",
    "Spray polyurethane foam (SPF) seamless roof systems",
    "Manufacturer-backed warranties up to 20 years",
    "Property-manager and HOA preferred for capital budgets",
  ],
  servicesH1: "Roof Coating & Restoration Services",
  servicesIntro:
    "Coating restoration is the best-kept secret in commercial roofing — and now in residential flat roofs too. Here's where coatings work, what they cost, and how long they last.",
  servicesOutro:
    "Wondering if your roof can be coated instead of replaced? Request a free condition assessment.",
  pillarBody: [
    "Coatings restore aging flat, low-slope, and metal roofs at 40-70% of full-replacement cost while delivering another 10-20 years of service. The math is straightforward: if your substrate is dry and structurally sound, coating beats replacement on every measure — cost, disruption, downtime, and warranty.",
    "But coatings are unforgiving of bad substrate. Wet insulation has to come out before coating. Ponding water rules out everything except silicone. Restaurant grease and oil exposure rules out acrylic. Metal needs rust-treated and primed before topcoat. The biggest reason coating projects fail in year three isn't the coating — it's a substrate condition that should have been caught and addressed before spray-day.",
    "We run dedicated coating crews trained on substrate prep, manufacturer specs, and warranty documentation. Every coating quote starts with a free condition assessment: core samples to test for trapped moisture, infrared scan where the building allows, and an honest call on whether coating or replacement is the right move for your roof.",
  ],
  services: [
    detail(
      "silicone-roof-coatings",
      "Silicone Roof Coatings",
      "Silicone Roof Coatings | Ponding-Water Restoration",
      "Silicone roof coatings for flat and low-slope roofs — ponding-water rated with 20-year warranties.",
      "Silicone Roof Coatings",
      "Silicone is the only coating that thrives in ponding water — which is why it's the right call on most Florida flat roofs.",
      [
        "Single-coat silicone over prepared substrate",
        "Ponding-water rated (most coatings fail here)",
        "Adds 10–20 years to a sound roof",
        "20-year manufacturer warranties available",
        "Bright white cool-roof reflective finish",
        "No tear-off — meaningful disruption savings",
      ],
      [
        "Silicone runs $3–$6 per square foot installed — typically 40–70% of replacement cost.",
        "We use GE Enduris, Mule-Hide ProCoat SF, and other tier-one silicone systems backed by manufacturer warranties from 10 to 20 years. Warranty length depends on millage applied (more silicone = longer warranty), substrate condition at install, and re-coat schedule.",
      ],
      [
        { q: "Why silicone over acrylic?", a: "Silicone handles ponding water; acrylic doesn't." },
        { q: "Can silicone be applied over existing coating?", a: "Yes when the existing coating is sound and clean. We power-wash and prime before topcoat." },
        { q: "Re-coat schedule?", a: "Silicone resets the warranty clock with a single re-coat every 15-20 years at ~50% of original install cost." },
      ],
    ),
    detail(
      "acrylic-roof-coatings",
      "Acrylic Roof Coatings",
      "Acrylic Roof Coatings | Reflective Cool-Roof Systems",
      "Acrylic roof coatings — reflective cool-roof systems for metal and well-drained membrane roofs.",
      "Acrylic Roof Coatings",
      "Affordable, bright-white, and energy-saving — the right call on metal and well-drained membrane roofs.",
      [
        "Water-based, low-VOC",
        "Bright white reflective finish",
        "Cool-roof rated — lowers attic temps 20–40°F",
        "10-year warranties typical",
        "ENERGY STAR and CRRC listed",
        "Lower cost than silicone where ponding isn't a factor",
      ],
      [
        "Acrylic runs $2–$4 per square foot installed. Lower cost than silicone but not for ponding-water applications.",
        "Acrylic is the right call when energy savings drive the project: a well-drained metal warehouse roof in Central Florida saw HVAC bills drop 28% in the first summer after our acrylic application. Reflectivity drops over time — bright-white acrylic typically holds 70%+ reflectance through year five and warrants the re-coat at year eight.",
      ],
      [
        { q: "When is acrylic the right call?", a: "On metal, well-drained membrane, and sloped flat roofs where energy savings drive the project." },
        { q: "Does acrylic handle Florida UV?", a: "Yes — but it chalks over time and needs re-coat earlier than silicone." },
      ],
    ),
    detail(
      "metal-roof-restoration",
      "Metal Roof Restoration",
      "Metal Roof Restoration | Rust Treatment & Coating",
      "Metal roof restoration — rust treatment, fastener replacement, and elastomeric coating systems.",
      "Metal Roof Restoration",
      "Aging exposed-fastener metal roof? Restoration buys you another 15–20 years at 30–40% of replacement cost.",
      [
        "Rust treatment and primer",
        "Failed fastener replacement (typically 100% of fasteners)",
        "Seam re-sealing with butyl tape and topcoat",
        "Elastomeric or silicone topcoat",
        "Florida Product Approved coating systems",
        "Restoration paint warranty separate from coating warranty",
      ],
      [
        "Metal restoration runs $4–$7 per square foot — meaningful savings vs full re-roof when the panels themselves are sound.",
        "On a typical Florida 5V crimp roof, fasteners fail before panels do — gaskets dry-rot in 15-20 years and start leaking at the screw head. Restoration replaces every fastener, seals every seam, treats rust, primes, and topcoats. The result is effectively a new roof at 30-40% of replacement cost.",
      ],
      [
        { q: "When is restoration not enough?", a: "When panels are perforated, structurally rusted, or substrate damage exists. We tell you straight." },
        { q: "Do you replace fasteners on every roof?", a: "Yes — every fastener replaced as part of the restoration scope. Re-using old fasteners is the #1 reason metal restoration projects leak in year two." },
      ],
    ),
    detail(
      "foam-roofing",
      "Spray Polyurethane Foam (SPF) Roofing",
      "Spray Polyurethane Foam (SPF) Roofing | Seamless Insulation + Roof",
      "Spray polyurethane foam (SPF) roofing — seamless insulation and roof in one system, topcoated with silicone or acrylic.",
      "Spray Polyurethane Foam (SPF) Roofing",
      "Foam plus topcoat is the highest-R-value, most-watertight flat roof system available.",
      [
        "Closed-cell spray polyurethane foam",
        "Seamless — no laps, no fasteners, no seams to fail",
        "R-value of 6+ per inch (highest of any roof)",
        "Silicone or acrylic topcoat",
        "Self-flashing — penetrations sprayed in",
        "Indefinite life with re-coat every 15-20 years",
      ],
      [
        "SPF systems run $8–$14 per square foot installed and dominate when both insulation and roof are needed in one system.",
        "SPF is the right call when the existing roof has inadequate insulation, complex penetrations, or chronic ponding. The foam self-levels into ponding areas, flashes around penetrations without separate flashing details, and adds meaningful R-value to the roof envelope.",
      ],
      [
        { q: "How long does SPF last?", a: "Indefinitely when re-coated every 15–20 years." },
        { q: "Can you apply SPF over existing roof?", a: "Yes when the substrate is dry and clean. We core-sample first to confirm." },
      ],
    ),
    detail(
      "roof-recoat",
      "Roof Re-Coat",
      "Roof Re-Coat | Extending Existing Coating Life",
      "Roof re-coat — refresh existing coating systems to extend roof life another 10–15 years.",
      "Roof Re-Coat",
      "Existing coating chalking or thinning? Re-coat resets the warranty clock at 40–60% of original install cost.",
      [
        "Clean, prime, and re-coat",
        "Resets warranty clock 10–15 years",
        "40–60% of original install cost",
        "No tear-off, no disruption",
        "Same-coating or upgrade-tier re-coat options",
        "Fresh manufacturer warranty on re-coat",
      ],
      [
        "Re-coats run $2–$5 per square foot depending on existing condition and topcoat system.",
        "Re-coat is when an existing coating tells you it's ready: surface chalk, reduced reflectance, hairline cracking at terminations, or simply hitting year 10-12 with no failures. Re-coating before failures appear keeps the substrate dry and the next 15-year warranty intact.",
      ],
      [
        { q: "How do I know when to re-coat?", a: "When the existing coating is chalking, thin, or the warranty is approaching its end." },
        { q: "Can I upgrade from acrylic to silicone at re-coat?", a: "Yes — many of our re-coats are tier-upgrades from acrylic to silicone for ponding-water performance." },
      ],
    ),
    detail(
      "epdm-membrane-restoration",
      "EPDM Membrane Restoration",
      "EPDM Rubber Roof Restoration & Coating | Florida",
      "EPDM rubber roof restoration with primer and elastomeric topcoat — extend life 10-15 years at 40% of replacement cost.",
      "EPDM Membrane Restoration",
      "Aging black EPDM roof leaking at seams? Primer + elastomeric topcoat seals the membrane and adds 10-15 years.",
      [
        "EPDM-specific primer and topcoat system",
        "Seam re-strip with butyl tape under coating",
        "Bright-white reflective finish (energy savings vs black EPDM)",
        "Penetration flashing repair included",
        "10-15 year manufacturer warranties",
        "40% of full replacement cost",
      ],
      [
        "EPDM restoration runs $3–$5 per square foot. Black EPDM absorbs heat (rooftop temps 150°F+ in Florida summer); restoration to a bright-white reflective finish drops attic and rooftop temperatures dramatically and often cuts cooling costs by 20%+.",
        "We address every seam before topcoat — old EPDM seam adhesive becomes the primary failure point in years 15-20. Re-stripping with butyl tape under the coating creates a redundant seal that outlasts the coating warranty.",
      ],
      [
        { q: "Will coating fix a leaking EPDM seam?", a: "Yes when the seam is dry, prepped, and re-stripped. Coating alone over a wet seam will fail." },
      ],
    ),
    detail(
      "tpo-pvc-restoration",
      "TPO & PVC Membrane Restoration",
      "TPO & PVC Single-Ply Roof Restoration & Coating | Florida",
      "TPO and PVC single-ply roof restoration — coating systems that extend life at a fraction of replacement cost.",
      "TPO & PVC Membrane Restoration",
      "Aging TPO or PVC roof showing seam failures or surface wear? Coating restoration extends life without a tear-off.",
      [
        "TPO- and PVC-compatible primer and topcoat",
        "Heat-weld seam repair before coating",
        "Penetration flashing re-detail",
        "Restoration coating warranty (separate from original membrane warranty)",
        "Bright-white reflective finish",
        "40-50% of full replacement cost",
      ],
      [
        "TPO and PVC single-ply membranes typically come up for tear-off at 18-22 years in Florida. Restoration coating in years 15-18, before the membrane begins to fail, is the most cost-effective move available — adding 10-15 years and a fresh warranty at half the cost of replacement.",
      ],
      [
        { q: "Can I restore my TPO roof if it's already leaking?", a: "We can — but only after every seam is heat-weld repaired and the membrane is dry. Wet insulation has to come out first." },
      ],
    ),
  ],
  aboutH1: "About Coating Kingz",
  aboutBody: [
    "CoatingKingz.com is the coatings-and-restoration brand operated by O'Brien Contracting, a fully licensed Florida roofing contractor (License CCC1335947).",
    "Coatings are specialist work. We run dedicated coating crews trained on substrate preparation, manufacturer specs, and warranty documentation.",
    "Every coating job is backed by our workmanship guarantee plus the manufacturer's coating warranty.",
    "We work with Florida property managers, HOA boards, and commercial owners on capital-planning budgets — written 5- and 10-year roof condition assessments and re-coat schedules are part of our standard work.",
    "We're approved applicators for GE Enduris, Mule-Hide ProCoat, GAF Unisil, Tropical Roofing Products, and other tier-one coating manufacturers. Approved-applicator status is what unlocks the longer-tail warranties (15-20 years) that non-approved coating contractors can't offer.",
  ],
  faqs: [
    { q: "What roofs can be coated?", a: "Metal, modified bitumen, BUR, EPDM, TPO, PVC, foam, and concrete flat roofs — when the substrate is sound." },
    { q: "How long do coatings last?", a: "10–20 years depending on system, application, and re-coat schedule." },
    { q: "Cheaper than replacement?", a: "Typically 40–70% less, with no tear-off and minimal business disruption." },
    { q: "Can coating fix a leak?", a: "Coating seals leak paths once the source is addressed — wet insulation has to come out first." },
    { q: "Manufacturer warranties?", a: "Yes — silicone systems up to 20 years, acrylic up to 10, foam systems indefinitely with re-coats." },
    { q: "Do you test for wet insulation before coating?", a: "Yes — core samples, and infrared scans where the building allows. Coating over wet insulation is the #1 way coating projects fail." },
    { q: "Will coating void my existing roof warranty?", a: "Sometimes — we check with your existing manufacturer before quoting and recommend coating systems compatible with the warranty when possible." },
    { q: "What's the difference between coating and a recover?", a: "Coating is sprayed/rolled liquid that cures into a seamless membrane. A recover installs a new membrane (TPO, PVC, modified) over the existing roof. Coating is faster, cheaper, and disruption-free; recover lasts longer and carries a longer warranty." },
  ],
  extraTools: [],
};

/** Generic O'Brien-branded microsite content for OBC-named domains. */
const ORU: MicroSiteContent = {
  heroH1: "O'Brien Roofing USA — Licensed Roofing Contractor",
  heroSubhead:
    "Residential and commercial roofing across Florida and beyond — licensed, insured, lifetime workmanship guarantee.",
  heroBullets: [
    "Free written estimates",
    "Insurance claim assistance",
    "Financing available (as low as $99/mo, OAC)",
    "Lifetime workmanship guarantee",
    "Florida-licensed (CCC1335947), bonded, insured",
    "Manufacturer-certified GAF, Owens Corning, CertainTeed installers",
  ],
  servicesH1: "O'Brien Roofing Services",
  servicesIntro:
    "We install, replace, and repair every major roof system. Here are the services we deliver most.",
  servicesOutro:
    "Need a quote? Most quotes are scheduled within two business days.",
  pillarBody: [
    "O'Brien Roofing is a full-service Florida roofing contractor — residential and commercial, every system, every county we serve. Architectural and impact-rated shingle, standing-seam and 5V crimp metal, concrete and clay tile, TPO/PVC/modified flat membranes, silicone and acrylic coating restoration, and storm-damage insurance work.",
    "Every project — from a $500 leak repair to a $500,000 commercial re-roof — is installed by O'Brien Contracting in-house crews. We don't subcontract out to day-labor firms, we don't run a high-pressure sales floor, and we don't ask homeowners to commit to work before they have a written, itemized quote in hand.",
    "Our work is backed by a lifetime workmanship guarantee on every roof we touch, plus the manufacturer's material warranty (up to 50 years on shingle, lifetime on metal). Pull a permit, install to current Florida Building Code, document the wind-mit credits, hand off the warranty paperwork — that's the model.",
  ],
  services: [
    detail(
      "residential-roofing",
      "Residential Roofing",
      "Residential Roofing | Shingle, Metal, Tile, Flat",
      "Residential roofing services — shingle, metal, tile, and flat systems with lifetime workmanship guarantee.",
      "Residential Roofing",
      "Every major residential roof system, installed and warrantied.",
      [
        "Architectural and impact-rated shingle",
        "Standing seam and 5V metal",
        "Concrete and clay tile",
        "TPO, PVC, and modified bitumen flat",
        "Manufacturer-certified installers across all major brands",
        "Fresh wind-mit form on every install",
      ],
      [
        "Residential roof projects start with a free written estimate including system options, material grades, and a fixed price.",
        "Most residential replacements complete in 1-3 days for shingle and 3-7 days for tile and metal. Every project includes deck inspection, rotten plywood replacement at a quoted per-sheet price, synthetic underlayment, peel-and-stick valleys, and current FBC fastening.",
        "Every roof we install is permitted, inspected, and closed through the local building department. We pull every permit in our name — homeowners never get asked to permit work themselves.",
      ],
      [
        { q: "Free estimates?", a: "Yes — every initial inspection and quote is free and written." },
        { q: "How long is the quote good for?", a: "30 days, held at the quoted price. No high-pressure 'today only' pricing." },
        { q: "Do you handle HOA approvals?", a: "Yes — we route color/material specs through architectural review boards and align permit timing with HOA cycles." },
      ],
    ),
    detail(
      "commercial-roofing",
      "Commercial Roofing",
      "Commercial Roofing | TPO, PVC, Modified, Metal, Coatings",
      "Commercial roofing services — TPO, PVC, modified bitumen, metal, and coating restoration systems.",
      "Commercial Roofing",
      "Property managers, HOAs, REITs, and owners — we run dedicated commercial crews.",
      [
        "TPO, PVC, EPDM single-ply",
        "Modified bitumen and BUR",
        "Commercial metal systems",
        "Silicone and acrylic coatings",
        "Capital-planning condition assessments (5- and 10-year)",
        "Bonded and insured for commercial work",
      ],
      [
        "Commercial bids include written scope, written warranty, and named project manager.",
        "We work with Florida property managers running multi-building portfolios, HOA boards on multifamily roofs, REIT asset managers on retail and industrial, and individual commercial owners. Every bid is built around a single named project manager who owns the project end-to-end — no hand-offs, no surprise crew changes.",
      ],
      [
        { q: "Bonded?", a: "Yes — bonded and insured for commercial work." },
        { q: "Multi-property contracts?", a: "Yes — we work with property managers running 10+ buildings across Florida." },
        { q: "Capital-planning budgets?", a: "Yes — written 5- and 10-year roof condition assessments are part of our work." },
      ],
    ),
    detail(
      "roof-replacement",
      "Roof Replacement",
      "Roof Replacement | Full Tear-Off, Code Compliant",
      "Full roof replacement — tear-off, deck inspection, hurricane-rated fastening, fresh wind-mit form.",
      "Roof Replacement",
      "Aging roof? We replace to current code with full documentation.",
      [
        "Full tear-off to deck",
        "Rotten deck replacement included (itemized per sheet)",
        "Current code fastening",
        "Synthetic underlayment + peel-and-stick valleys",
        "Fresh wind-mit form",
        "Lifetime workmanship guarantee",
      ],
      [
        "Replacement quotes are written and itemized — system options, material grades, and a fixed price.",
        "Every replacement we do is built to maximize wind-mitigation credits on your insurance carrier's form: secondary water resistance over the entire deck, 6-nail high-wind fastening, sealed roof-to-wall connections. The discount line items routinely cover the upgrade cost within 2-4 years.",
      ],
      [
        { q: "Financing?", a: "Yes — as low as $99/mo subject to credit approval." },
        { q: "How long does it take?", a: "1-3 days for shingle, 3-7 days for tile and metal." },
      ],
    ),
    detail(
      "roof-repair",
      "Roof Repair",
      "Roof Repair | Leaks, Storm Damage, Component Repair",
      "Roof repair — leaks, wind-damaged components, valley and flashing repair across major roof systems.",
      "Roof Repair",
      "Active leak or storm damage? Most repairs are same-week scheduling.",
      [
        "Leak diagnosis and repair",
        "Wind-damaged component replacement",
        "Valley and flashing repair",
        "Pipe boot and skylight re-flashing",
        "Photographed before and after",
        "Written summary for your insurance file",
      ],
      [
        "Repairs are quoted in writing with photos.",
        "Most Florida roof leaks aren't where the stain is — water tracks along the deck for feet or yards before dropping. We start with a full-roof inspection (not just the slope above the stain) so the repair addresses the actual source.",
      ],
      [
        { q: "Emergency tarping?", a: "Yes — we tarp same-day after storms when needed." },
        { q: "What's the typical repair cost?", a: "Most Florida residential repairs run $300–$2,500 depending on roof system and access." },
      ],
    ),
    detail(
      "insurance-claim-roofing",
      "Insurance Claim Roofing",
      "Insurance Claim Roofing | Adjuster Meetings, Documentation",
      "Insurance claim roofing services — damage documentation, adjuster meetings, replacement to spec on approved claims.",
      "Insurance Claim Roofing",
      "Storm damage? Hail event? We move you from damage to replacement with the carrier paying the bulk.",
      [
        "Free documented damage inspection",
        "Emergency tarping where needed",
        "We meet your adjuster on-site",
        "Full photo and measurement documentation",
        "Replacement to spec on approved claims",
        "We only collect from the carrier plus your deductible",
      ],
      [
        "We only collect from the carrier plus your deductible — never asking homeowners to pay out of pocket for covered claims.",
        "We don't take Assignment of Benefits (AOB) — Florida AOB has been a source of consumer abuse and we don't participate. We work for the homeowner, not the carrier.",
      ],
      [
        { q: "What if the claim is denied?", a: "We help you supplement with additional documentation when warranted." },
        { q: "How long does a claim take?", a: "Initial inspection within 24-48 hours; carrier response varies (typically 14-30 days)." },
      ],
    ),
    detail(
      "roof-inspection",
      "Roof Inspection",
      "Roof Inspection | Free Written Reports & Wind-Mit Forms",
      "Free written roof inspections across Florida — condition assessment, wind-mit forms, real-estate transaction reports.",
      "Roof Inspection",
      "Buying a home, selling a home, or trying to figure out what's wrong with your roof? Inspections are free and written.",
      [
        "Free written condition report",
        "Slope-by-slope photo documentation",
        "Wind-mitigation form preparation",
        "Real-estate transaction reports",
        "Insurance carrier re-inspection support",
        "Drone imagery on complex or steep roofs",
      ],
      [
        "Every inspection is free, written, and held in our system if you need a copy later. We photograph every slope, document every penetration, and report on remaining useful life — not just whether the roof is leaking today.",
        "If you need a wind-mit form for your insurance carrier, we prepare it during the inspection and email it directly to the carrier on your authorization.",
      ],
      [
        { q: "How long does an inspection take?", a: "Typically 30-60 minutes on a residential roof." },
        { q: "Will you report leaks you didn't cause?", a: "Yes — written honestly, with photos. We're not in the business of upselling repairs that aren't needed." },
      ],
    ),
    detail(
      "emergency-tarping",
      "Emergency Roof Tarping",
      "Emergency Roof Tarping | Same-Day Storm Response",
      "Same-day emergency roof tarping across Florida — protect your home from further water damage after a storm.",
      "Emergency Roof Tarping",
      "Active leak after a storm or wind event? Same-day tarping in most of our service area.",
      [
        "Same-day response in most service areas",
        "Heavy-gauge tarps with sandbag anchoring",
        "Breathable mesh tarps for tile roofs",
        "Photo documentation for insurance",
        "Free tarp on insurance-claim projects",
      ],
      [
        "Tarping is the first line of defense after a storm — getting the water out of the home while we figure out the long-term repair or insurance path. We use heavy-gauge tarps anchored with sandbags (not screws into the deck) on shingle and modified roofs, and breathable mesh on tile to avoid trapping moisture against the deck.",
        "Every tarping job is photographed and documented for insurance. If we end up doing the full replacement under your claim, the tarp is free.",
      ],
      [
        { q: "How long will a tarp last?", a: "30-90 days depending on weather and anchoring method." },
        { q: "Will my insurance reimburse tarping cost?", a: "Most Florida HO-3 policies cover reasonable mitigation costs after a covered loss. We invoice the carrier directly when you authorize." },
      ],
    ),
  ],
  aboutH1: "About O'Brien Roofing USA",
  aboutBody: [
    "O'Brien Roofing USA is operated by O'Brien Contracting, a fully licensed Florida roofing contractor (License CCC1335947).",
    "Every project is installed by in-house crews and backed by our lifetime workmanship guarantee.",
    "We serve homeowners, property managers, HOAs, and commercial owners across Florida.",
    "We're manufacturer-certified by GAF, Owens Corning, and CertainTeed for shingle systems, and approved installers for every major metal, tile, and single-ply commercial manufacturer operating in Florida. Certification matters because it unlocks the longer-tail manufacturer warranties (up to 50 years on shingle, lifetime on metal) that uncertified roofers can't offer.",
    "We pull permits in our name on every project, install to current Florida Building Code, document wind-mitigation credits for your insurance carrier, and hand off complete warranty paperwork at close-out.",
  ],
  faqs: [
    { q: "Are you licensed?", a: "Yes — Florida license CCC1335947 plus general liability and workers' comp." },
    { q: "Free estimates?", a: "Yes — every quote is free and written." },
    { q: "Financing?", a: "Yes — as low as $99/mo subject to credit approval." },
    { q: "Warranty?", a: "Lifetime workmanship plus manufacturer's material warranty." },
    { q: "Service area?", a: "Statewide Florida." },
    { q: "Do you pull permits?", a: "Yes — every project permitted, inspected, and closed through the local building department." },
    { q: "Do you take Assignment of Benefits (AOB)?", a: "No — we work for the homeowner. Florida AOB has been a source of consumer abuse." },
    { q: "Manufacturer certifications?", a: "GAF, Owens Corning, CertainTeed for shingle; approved-installer status with every major metal, tile, and commercial manufacturer." },
  ],
  extraTools: ["storm-damage-analyzer"],
};

const ORFL: MicroSiteContent = {
  ...ORU,
  heroH1: "O'Brien Roofing FL — Your Local Florida Roofing Team",
  heroSubhead:
    "Florida-licensed roofing contractor serving Sarasota, Charlotte, Lee, Collier, Manatee, Miami-Dade, Broward, and Palm Beach counties.",
  heroBullets: [
    "Free written estimates statewide",
    "Insurance claim assistance with every Florida carrier",
    "Financing as low as $99/mo (OAC)",
    "Lifetime workmanship guarantee",
    "Florida-licensed CCC1335947, bonded, insured",
    "Hurricane-rated roof systems built for Florida wind zones",
  ],
  pillarBody: [
    "O'Brien Roofing FL is the Florida-focused arm of O'Brien Contracting — built specifically around how Florida roofs fail, what Florida insurance carriers reward, and what Florida building code requires. We work every coast: Gulf side from Tampa down through Naples, Atlantic from Daytona through Miami, and the Keys for tile and metal specialty work.",
    "Florida roofs operate in the harshest residential roofing environment in the United States: 95°F+ rooftop temperatures, salt-air corrosion within ten miles of either coast, UV that bakes shingles faster than the manufacturer's national-average warranty assumes, and named-storm uplift events every summer. Every roof we install is engineered for those conditions — Florida Product Approved materials, current-FBC fastening, secondary water resistance, and a fresh wind-mitigation form documenting every insurance discount.",
    "Every project is installed by O'Brien Contracting in-house crews, permitted in our name, and closed through the local building department. We don't subcontract, we don't run high-pressure sales, and we don't ask homeowners to permit work themselves.",
  ],
  aboutH1: "About O'Brien Roofing FL",
  aboutBody: [
    "O'Brien Roofing FL is the Florida-focused brand operated by O'Brien Contracting, a fully licensed Florida roofing contractor (License CCC1335947).",
    "Headquartered in Florida and serving every coast — Gulf, Atlantic, and the Keys — with in-house crews and the same lifetime workmanship guarantee on every job.",
    "We're manufacturer-certified by GAF, Owens Corning, and CertainTeed for shingle, and approved installers for every major metal and tile mill operating in Florida. Certification matters because it unlocks longer-tail manufacturer warranties (up to 50 years on shingle, lifetime on metal).",
    "Counties we serve directly with in-house crews: Sarasota, Manatee, Charlotte, Lee, Collier, Miami-Dade, Broward, and Palm Beach. We extend across the rest of the state through our statewide service network on larger commercial projects.",
    "Every project is permitted, inspected, and closed through the local building department. We pull every permit in our name and document wind-mitigation credits for your insurance carrier on completion.",
  ],
};

export const MICRO_SITE_CONTENT: Record<Exclude<BrandKey, "obc" | "rcf">, MicroSiteContent> = {
  frr: FRR,
  fcr: FCR,
  ftr: FTR,
  fsr: FSR,
  fmr: FMR,
  ck: CK,
  oru: ORU,
  orfl: ORFL,
};

export const getMicroSiteContent = (brand: BrandKey): MicroSiteContent | null => {
  if (brand === "obc" || brand === "rcf") return null;
  return MICRO_SITE_CONTENT[brand];
};
